<template>
  <page :loading="$apollo.queries.project.loading || !project" :err="err" layoutClass="job-show">
    <b-row class="mb-3" v-if="project">
      <b-col class="px-md-5 p-2" md="10" offset="1">

        <!-- project details card -->
        <b-card>

          <!-- image -->
          <!-- img
            v-if="project.image"
            class="w-100"
            style="max-height: 350px"
            :src="project.image"
          / -->
          <!-- title and location -->
          <div class="mt-3">
            <h4
              v-if="project.location"
              style="font-weight: 500"
            >
              <font-awesome-icon class="text-muted" icon="map-marker"/> {{ project.location.name }}, {{ project.location.countryCode }}
            </h4>
            <h3><strong>{{ project.title }}</strong></h3>
          </div>
          <!-- description -->
          <markdown v-if="showFullDescription || project.status !== 'ACTIVE'" :source="project.description" :anchorAttributes="markdownAnchorAttributes"/>
          <markdown v-else :source="project.excerpt" :anchorAttributes="markdownAnchorAttributes"/>

          <!-- dates -->
          <div
            v-if="project.startDate && project.endDate"
            class="d-flex justify-content-between align-items-center my-3"
          >
            <span><span class="text-muted">Start</span> <b>{{ formatDate(project.startDate, 'DD.MM.YYYY') }}</b></span>
            <font-awesome-icon icon="long-arrow-alt-right" />
            <span><span class="text-muted">End</span> <b>{{ formatDate(project.endDate, 'DD.MM.YYYY') }}</b></span>
          </div>

          <b-card-footer class="bg-white border-0" v-if="project.status === 'ACTIVE'">
            <h4>
              <a href="#" @click.prevent="toggleDescription">
                <template v-if="!showFullDescription">Show Description</template>
                <template v-else>Hide Description</template>
              </a>
            </h4>
          </b-card-footer>

        </b-card>

        <b-card class="mt-3" v-if="project.status !== 'ACTIVE'">
          <b-alert v-if="project.status === 'INACTIVE'" show variant="warning">Sorry, this project is currently inactive.</b-alert>
          <b-alert v-else-if="project.status === 'DELETED'" show variant="warning">Sorry, this project has been deleted.</b-alert>
        </b-card>

        <!-- Recruiter buttons -->
        <b-card class="mt-3" v-if="isRecruiter">
          <b-button
            v-if="showAddProjectsButton"
            variant="primary"
            :to="{ name: 'ProjectEdit', params: { projectID: project.id } }"
          >
            <font-awesome-icon icon="pencil-alt"/> Edit Project
          </b-button>
          <b-button
            v-if="showAddProjectsButton"
            variant="primary"
            class="ml-2"
            @click="$router.push({
              name: 'RoleNew',
              params: { projectID: project.id },
              query: { startDate: project.startDate, endDate: project.endDate }
            })"
          >
            <font-awesome-icon icon="plus"/> Add Role
          </b-button>
          <!-- b-button
            v-if="showAddProjectsButton"
            variant="primary"
            class="ml-2"
            @click="$bvModal.show('import-roles-modal')"
          >
            <font-awesome-icon icon="download"/> Attach Roles
          </b-button -->
        </b-card>

        <!-- available roles card -->
        <internal-jobs-list
          v-if="project.status === 'ACTIVE' || isRecruiter"
          class="mt-4"
          layout="page"
          :highlightedID="highlightedID"
          :loading="$apollo.loading || !jobs"
          :whereCustomFields="jobs ? jobs.jobAggs : null"
          :where="filterJobsWhere"
          @whereUpdated="filterJobsWhereUpdated"
          :results="jobs ? jobs.jobConnections : null"
          @pageUpdated="filterJobsPageUpdated"
          :order="filterJobsOrder"
          @orderUpdated="filterJobsOrderUpdated"
          :isProjectDisplay="false"
        />
        <!-- b-card v-if="Array.isArray(project.jobs)" class="mt-3">
          <b-alert v-if="project.status === 'INACTIVE'" show variant="warning">Sorry, this project has been closed.</b-alert>
          <b-alert v-else-if="project.status === 'DELETED'" show variant="warning">Sorry, this project has been deleted.</b-alert>
          <template v-if="project.status === 'ACTIVE' || isRecruiter">
            <b-row class="pb-3 border-bottom">
              <b-col class="block-title">Available Roles</b-col>
            </b-row>
            <b-row
              v-for="job in project.jobs"
              class="border-bottom py-0 my-0"
              :key="job.id"
              :id="`job${job.id}`"
            >
              <job-list-item :job="job" :noBorder="true" />
            </b-row>
          </template>
        </b-card -->
      </b-col>

    </b-row>

    <!-- Mentor Request Details Modal -->
    <b-modal
      v-if="isRecruiter && project"
      :id="'import-roles-modal'"
      title="Attach Roles"
      body-class="gray-bg"
      size="lg"
      hide-footer
      lazy
    >
      <import-roles-container
        :projectID="project.id"
      />
    </b-modal>
  </page>
</template>

<script>
import base64 from 'base-64';
import ms from 'ms';
import gql from 'graphql-tag';
import JobDetailsFragment from '@/graphql/fragments/JobDetails.gql';
import Markdown from 'vue-markdown';
import { mapRoot } from '@src/mixins/root';
import formatDate from 'date-fns/format';

import Page from '@/components/Page';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import ProjectShowQuery from '@/graphql/queries/ProjectShow.gql';
import ProjectRolesShowQuery from '@/graphql/queries/ProjectRolesShow.gql';
import JobListItem from '@/components/jobs/UserMatchListItem';
import InternalJobsList from '@/components/projects/InternalJobsListContainer';
import ImportRolesContainer from '@/components/projects/ImportRolesContainer';

const DetachJobMutation = gql`
  ${JobDetailsFragment}

  mutation DetachJob($jobID: ID!) {
    updateJob(update: { project: null, isInternal: true }, where: { id: $jobID }) {
      ...JobDetails
    }
  }
`;

export default {
  props: {
    projectID: String,
  },
  components: {
    Page,
    LoadingSpinner,
    Markdown,
    JobListItem,
    InternalJobsList,
    ImportRolesContainer,
  },
  created() {
    if (this.company && this.company.sitePreferences) {
      const { company: { sitePreferences } } = this;
      this.filterJobsOrder = sitePreferences.defaultInternalJobOrderBy || this.filterJobsOrder;
      this.filterJobsOrder = this.$route.query ? (this.$route.query.order || this.filterJobsOrder) : this.filterJobsOrder;
    }

    if (this.$route && this.$route.query && this.$route.query.filter) {
      const { where, order, page, limit } = JSON.parse(base64.decode(this.$route.query.filter));
      this.$log.debug({ where, order, page, limit });

      if (typeof where === 'object') {
        this.filterJobsWhere = { ...this.filterJobsWhere, ...where };
      }
      if (typeof order === 'string') {
        this.filterJobsOrder = order;
      }
      if (typeof page === 'number') {
        this.filterJobsPage = page;
      }
      if (typeof limit === 'number') {
        this.filterJobsLimit = limit;
      }
    }
  },
  data() {
    const { projectID } = this;
    return {
      err: null,
      showFullDescription: false,
      showApplyBox: false,
      applyResult: null,
      filterJobsWhere: {
        isInternal: true,
        project: {
          id: projectID,
        },
        status: 'ACTIVE',
      },
      filterJobsPage: 1,
      filterJobsLimit: 20,
      filterJobsOrder: 'CREATED_DESC',
      highlightedID: this.$route.query && this.$route.query.jobID ? this.$route.query.jobID : null,
    };
  },
  apollo: {
    project: {
      query: ProjectShowQuery,
      variables() {
        const { projectID } = this;
        return {
          projectID,
        };
      },
      update({ company: { project } = {} }) {
        return project;
      },
      error(err) {
        this.$log.error(err);
      },
      deep: true,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      prefetch: true,
    },
    jobs: {
      query: ProjectRolesShowQuery,
      variables() {
        const { filterJobsWhere: where, filterJobsOrder: order, filterJobsPage: page, filterJobsLimit: limit } = this;
        return { where, order, page, limit };
      },
      update(data) {
        const { company: { jobConnections, jobAggs } = {} } = data;
        return { jobConnections, jobAggs };
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      deep: true,
      throttle: ms('1s'),
    },
  },
  mounted() {
    setTimeout(() => {
      if (this.$route.query && this.$route.query.jobID) {
        this.$scrollTo('#internalJobList', { offset: -20 });
      }
    }, 1000);
  },
  computed: {
    ...mapRoot([
      'user', 'company', 'isLoggedIn', 'isRecruiter', 'isSuperAdmin',
    ]),
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    showCvUploadBox() {
      const { showCvUploadBox = true } = this.company.sitePreferences;
      return showCvUploadBox;
    },
    showAddProjectsButton() {
      return true;
    },
  },
  metaInfo() {
    if (this.project) {
      const { title } = this.project;
      return { title };
    } else {
      return { title: 'Project' };
    }
  },
  methods: {
    filterJobsWhereUpdated(where) {
      if (JSON.stringify(this.filterJobsWhere) !== JSON.stringify(where)) {
        this.filterJobsWhere = where;
        this.filterJobsPage = 1;

        this.updateRouteFilter({ where });
        this.$scrollTo('body');
      }
    },
    filterJobsOrderUpdated(order) {
      this.filterJobsOrder = order;
      this.filterJobsPage = 1;

      this.updateRouteFilter({ page: undefined, order });
      this.$scrollTo('body');
    },
    filterJobsPageUpdated(page) {
      this.filterJobsPage = page;
      this.updateRouteFilter({ page });
      this.$scrollTo('body');
    },
    updateRouteFilter(values) {
      const existing = this.$route && this.$route.query ? this.$route.query.filter : null;
      this.$router.push({
        ...this.$route,
        query: {
          filter: base64.encode(JSON.stringify({
            ...(existing ? JSON.parse(base64.decode(existing)) : {}),
            ...values
          })),
        },
      });
    },
    toggleDescription() {
      this.showFullDescription = !this.showFullDescription;
      this.$scrollTo('#job-header');
    },
    formatDate,
    async detachJob(jobID) {
      const { projectID } = this;
      try {
        await this.$apollo.mutate({
          mutation: DetachJobMutation,
          variables: { jobID },
          update(store) {
            const data = store.readQuery({
              query: ProjectShowQuery,
              variables: {
                projectID,
                statusIn: [ 'ACTIVE', 'INACTIVE', 'DELETED' ],
              },
            });
            if (data && data.company && data.company.project && Array.isArray(data.company.project.jobs)) {
              data.company.project.jobs = data.company.project.jobs.filter(({ id }) => id !== jobID);
              store.writeQuery({
                query: ProjectShowQuery,
                variables: {
                  projectID,
                  statusIn: [ 'ACTIVE', 'INACTIVE', 'DELETED' ],
                },
                data,
              });
            }
          },
        });
      } catch (err) {
        this.$log.error(err);
        this.err = `${err}`;
      }
    },
  },
};
</script>
<style lang="scss">
.page .job-show {

  .block-title {
    text-transform: uppercase;
    font-weight: 600;
  }

  .loading-panel {
    margin: 20px 0 20px 0;
    text-align: center;
  }

  .horizontal-filter {
    margin-top: 1.4em;
  }
  .alert-danger {
    color: #721c24;
    background-color: #f8d7da;
    border-color: #f5c6cb;
  }

  .card {
    &.job-card {
      @media (min-width: 768px) {
        border-right: 0;
      }
      @media (max-width: 768px) {
        border-bottom: 0;
      }
    }

    .card-footer {
      text-align: center;
      h4 {
        vertical-align: middle;
      }
    }

    .job-edit-button {
      position: absolute;
      right: 15px;
    }

    .job-image {
      display: block;
      max-width: 150px;
      max-height: 150px;
      width: auto;
      height: auto;
    }

    .job-description {
      flex-direction: column;
      h1 {
        font-size: 1.3em;
      }
      h2 {
        font-size: 1.2em;
      }
    }

    .tags {
      display: inline-flex;
      .match-label {
        padding-left: 0.5em;
        padding-right: 0.5em;
        width: inherit;
        font-weight: 400;
        text-transform: inherit;
        margin-right: 0.7em;
        height: inherit;
      }
    }
  }

  .contacts-list {
    padding-top: 1em;
    display: flex;
    flex-direction: row;
    flex-flow: row wrap;
    margin-left: -1em;
    margin-right: -1em;
  }

  .filter-row {
    &.no-results {
      text-align: center;
    }
    .question-circle {
      margin: 0 auto;
      width: 80px;
      height: 80px;
      border-radius: 100%;
      color: white;
      vertical-align: middle;
      font-size: 50px;
      font-weight: bolder;
      span {
        text-align: center;
      }
    }
  }

  .contact-col {
    flex-basis: calc(100%/3);
    padding: 1em;
  }
  .upload-selector {
    max-width: 650px;
    margin: 0 auto;
    .btn:hover {
      opacity: 0.8;
    }
  }

  .sticky-top {
    top: 10px;
    z-index: 900;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/projects/ProjectShow.vue